<script setup lang="ts">
import { ScalarButton } from '@scalar/components/button'
import { ScalarTeleport } from '@scalar/components/teleport'
import { ScalarIconPlus } from '@scalar/icons'
import { POPULAR_CONTEXT_FUNCTION_KEYS } from '@scalar/workspace-store/request-example'
import type { XScalarEnvironment } from '@scalar/workspace-store/schemas/extensions/document/x-scalar-environments'
import { onClickOutside } from '@vueuse/core'
import Fuse from 'fuse.js'
import { computed, onMounted, ref, watch, type CSSProperties } from 'vue'

import { useLocalization } from '@/v2/features/localization'

const {
  query,
  environment,
  dropdownPosition,
  contextFunctionItems = [],
  listboxId,
} = defineProps<{
  query: string
  environment?: XScalarEnvironment
  dropdownPosition?: { left: number; top: number }
  /** Runtime placeholders such as `{{$guid}}`, shown with environment variables */
  contextFunctionItems?: { key: string; description: string }[]
  /**
   * Id for the `role="listbox"` element. When set, each option also receives a
   * stable id so the owning combobox input can reference the active option via
   * `aria-activedescendant` (W3C combobox pattern).
   */
  listboxId?: string
}>()

const emit = defineEmits<{
  (e: 'select', variable: string): void
  (e: 'redirect'): void
  /**
   * Fired when the user dismisses the panel by clicking outside. The owning
   * combobox needs this to clear its `aria-expanded`/`aria-controls` state,
   * which would otherwise keep pointing at a listbox that is no longer mounted.
   */
  (e: 'close'): void
}>()

const { translate } = useLocalization()

type DropdownRow =
  | { kind: 'env'; key: string; secondary: string }
  | { kind: 'context'; key: string; secondary: string }

const isOpen = ref(true)
const dropdownRef = ref<HTMLElement | null>(null)
const selectedVariableIndex = ref(0)

const redirectToEnvironment = () => {
  emit('redirect')
  isOpen.value = false
  // The panel is gone, so tell the combobox to drop its aria-expanded /
  // aria-controls — otherwise they keep pointing at a dismissed listbox.
  emit('close')
}

/** Normalize the variables to have a name and value */
const normalizedVariables = computed(() =>
  (environment?.variables ?? []).map((v) => ({
    key: v.name,
    value: typeof v.value === 'string' ? v.value : v.value.default,
  })),
)

const contextRows = computed((): DropdownRow[] =>
  contextFunctionItems.map((item) => ({
    kind: 'context',
    key: item.key,
    secondary: item.description,
  })),
)

const envRows = computed((): DropdownRow[] =>
  normalizedVariables.value
    .filter(({ key, value }) => key !== '' || value !== '')
    .map(({ key, value }) => ({
      kind: 'env' as const,
      key,
      secondary: value,
    })),
)

type FuseRow = DropdownRow & { fuseText: string }

const envFuseSource = computed((): FuseRow[] =>
  envRows.value.map((row) => ({
    ...row,
    fuseText: `${row.key} ${row.secondary}`,
  })),
)

const contextFuseSource = computed((): FuseRow[] =>
  contextRows.value.map((row) => ({
    ...row,
    fuseText: `${row.key} ${row.secondary}`,
  })),
)

const fuseEnv = computed(
  () =>
    new Fuse(envFuseSource.value, {
      keys: ['fuseText'],
      threshold: 0.35,
    }),
)

const fuseContext = computed(
  () =>
    new Fuse(contextFuseSource.value, {
      keys: ['fuseText'],
      threshold: 0.35,
    }),
)

const popularContextRows = computed((): DropdownRow[] => {
  const byKey = new Map(contextFunctionItems.map((c) => [c.key, c]))
  return POPULAR_CONTEXT_FUNCTION_KEYS.flatMap((key) => {
    const item = byKey.get(key)
    return item
      ? [
          {
            kind: 'context' as const,
            key: item.key,
            secondary: item.description,
          },
        ]
      : []
  })
})

const DROPDOWN_LIMIT = 12

const filteredVariables = computed((): DropdownRow[] => {
  if (!query.trim()) {
    const envSlice = envRows.value.slice(-4)
    return [...envSlice, ...popularContextRows.value]
  }

  const q = query.trim()
  const envMatches = fuseEnv.value
    .search(q, { limit: DROPDOWN_LIMIT })
    .map((res) => {
      const { kind, key, secondary } = res.item
      return { kind, key, secondary }
    })

  const remaining = DROPDOWN_LIMIT - envMatches.length
  const contextMatches =
    remaining > 0
      ? fuseContext.value.search(q, { limit: remaining }).map((res) => {
          const { kind, key, secondary } = res.item
          return { kind, key, secondary }
        })
      : []

  return [...envMatches, ...contextMatches]
})

const optionId = (index: number): string | undefined =>
  listboxId ? `${listboxId}-option-${index}` : undefined

/**
 * Id of the currently highlighted option, for the combobox's
 * `aria-activedescendant`. Undefined when there is no list to point at.
 */
const activeOptionId = computed((): string | undefined =>
  filteredVariables.value.length
    ? optionId(selectedVariableIndex.value)
    : undefined,
)

const selectVariable = (variableKey: string): void => {
  emit('select', variableKey)
}

// Narrowing the query shrinks the list; keep the highlight in range so both the
// visible selection and `aria-activedescendant` never reference a dropped option.
watch(
  () => filteredVariables.value.length,
  (length) => {
    if (selectedVariableIndex.value > length - 1) {
      selectedVariableIndex.value = Math.max(0, length - 1)
    }
  },
)

const handleArrowKey = (direction: 'up' | 'down') => {
  const offset = direction === 'up' ? -1 : 1
  const length = filteredVariables.value.length

  if (length === 0) {
    return
  }

  selectedVariableIndex.value =
    (selectedVariableIndex.value + offset + length) % length
}

const handleSelect = () => {
  if (selectedVariableIndex.value >= 0) {
    const selectedVariable =
      filteredVariables.value[selectedVariableIndex.value]
    if (selectedVariable) {
      selectVariable(selectedVariable.key)
    }
  }
}

defineExpose({
  handleArrowKey,
  handleSelect,
  activeOptionId,
})

onMounted(() => {
  // Reset selected index to the first item when dropdown opens
  selectedVariableIndex.value = 0
})

const dropdownStyle = computed<CSSProperties>(() => {
  return {
    left: (dropdownPosition?.left ?? 0) + 'px',
    // Add a 5px offset from the editor
    top: (dropdownPosition?.top ?? 0) + 5 + 'px',
  }
})

onClickOutside(
  dropdownRef,
  () => {
    isOpen.value = false
    emit('close')
  },
  { ignore: [dropdownRef] },
)
</script>
<template>
  <ScalarTeleport
    v-if="isOpen"
    class="scalar-client">
    <div
      ref="dropdownRef"
      class="custom-scroll z-context fixed top-0 left-0 flex max-h-[60svh] w-56 flex-col rounded border p-0.75"
      :style="dropdownStyle"
      @mousedown.prevent>
      <!--
        Always rendered (even with no matches) so the combobox's `aria-controls`
        always resolves to a real listbox; an empty list simply has no options.
      -->
      <ul
        :id="listboxId"
        :aria-label="
          translate(
            'apiClient.environmentVariablesDropdown.variableSuggestions',
          )
        "
        class="gap-1/2 flex flex-col"
        role="listbox">
        <template
          v-for="(item, index) in filteredVariables"
          :key="`${item.kind}-${item.key}`">
          <li
            :id="optionId(index)"
            :aria-selected="index === selectedVariableIndex"
            class="font-code text-xxs hover:bg-b-3 flex h-8 cursor-pointer items-center justify-between gap-1.5 rounded p-1.5 transition-colors duration-150"
            :class="{ 'bg-b-3': index === selectedVariableIndex }"
            role="option"
            @click="selectVariable(item.key)">
            <div class="flex items-center gap-2 whitespace-nowrap">
              <span
                v-if="item.kind === 'env'"
                class="h-2.25 w-2.25 min-w-2.25 rounded-full"
                :style="{
                  backgroundColor: environment?.color,
                }" />
              <span
                v-else
                class="text-c-3 bg-b-3 flex h-4.5 min-w-4.5 items-center justify-center rounded px-0.5 font-sans text-[9px] font-semibold">
                fn
              </span>
              {{ item.key }}
            </div>
            <span
              class="max-w-[9rem] overflow-hidden text-right text-ellipsis whitespace-nowrap">
              {{ item.secondary }}
            </span>
          </li>
        </template>
      </ul>
      <ScalarButton
        v-if="!filteredVariables.length"
        class="font-code text-xxs bg-b-inherit hover:bg-b-3 flex h-8 w-full justify-start gap-2 px-1.5 transition-colors duration-150"
        variant="outlined"
        @click="redirectToEnvironment">
        <ScalarIconPlus class="size-3" />{{
          translate('apiClient.environmentVariablesDropdown.addVariable')
        }}
      </ScalarButton>
      <!-- Backdrop for the dropdown -->
      <div
        class="bg-b-1 brightness-lifted absolute inset-0 -z-1 rounded shadow-lg" />
    </div>
  </ScalarTeleport>
</template>
